import * as React from 'react';
import { styled } from '@glitz/react';
import { translate, useMatchLargeViewport } from '@avensia/excite';
import { createMinLargeViewportDecorator } from 'webapp/viewport';
import RatingStars from './RatingStars';
import { EnergyLabel } from 'webapp/EnergyLabel';
import { addSelectContentEventToDataLayer } from 'webapp/tracking/events/content';
import Link from 'webapp/components/Link';
import Image from 'webapp/components/image';
import { CloseIcon } from 'webapp/components/icons';
import { Button } from 'webapp/components/button';
import { Theme } from 'webapp/theme';
import { ZIndex } from 'webapp/z-index';
import { monochromeDark } from 'svenssons/shared/theme';
import { useOnClickOutside } from '@avensia/excite-ui';

type PropType = NordicNest.Api.ProductPageApiModel & {
  scrollToReviews?: () => void;
};

export default React.memo(function ProductInformationHeader(props: PropType) {
  const isLargeViewport = useMatchLargeViewport();
  const [showEnergyInfo, setShowEnergyInfo] = React.useState(false);
  const energyInfoRef = React.useRef<HTMLDivElement>(null);
  useOnClickOutside(energyInfoRef, () => setShowEnergyInfo(false));

  const sendContentEvent = (url: string) => {
    addSelectContentEventToDataLayer({ id: props.productId, link: url, action: '', type: 'top_link' });
  };

  return (
    <Container>
      <DesignerAndBrand>
        {props.designerLinks?.length > 0 && (
          <Designers>
            {props.designerLinks.map((designer, i) => (
              <React.Fragment key={i}>
                {i !== 0 && <span>{', '}</span>}
                <DesignerLink key={designer.to} to={designer.to} onClick={() => sendContentEvent(designer.to)}>
                  {designer.title}
                </DesignerLink>
              </React.Fragment>
            ))}
            <span> {translate(t => t.product.forBrand)}</span>
          </Designers>
        )}
        <BrandLink to={props.brandUrl} onClick={() => sendContentEvent(props.brandUrl)}>
          <BrandName>{props.brand}</BrandName>
        </BrandLink>
      </DesignerAndBrand>
      <ProductName>
        {props.productName}
        <VariantName>{props.variantName}</VariantName>
      </ProductName>
      <RatingAndEnergyInfo>
        {props.testFreaksFamilyId?.length ? <Stars rating={props.rating} onClick={props.scrollToReviews} /> : <div />}
        {props.energyLabel && (
          <>
            <EnergyLabel
              label={props.energyLabel}
              onClick={isLargeViewport ? undefined : () => setShowEnergyInfo(true)}
              onMouseEnter={isLargeViewport ? () => setShowEnergyInfo(true) : undefined}
              alignRight
            />
            {showEnergyInfo && props.eprelUrl && (
              <EnergyInfoContainer onMouseLeave={isLargeViewport ? () => setShowEnergyInfo(false) : undefined}>
                <EnergyInfo ref={energyInfoRef}>
                  <CloseButton
                    as={Theme.Transparent}
                    onClick={() => setShowEnergyInfo(false)}
                    square
                    aria-label={translate(t => t.ariaLabelCloseButton)}
                  >
                    <CloseIcon />
                  </CloseButton>
                  <EnergyInfoContent>
                    {props.eprelUrl && (
                      <ProductInfoSheet
                        to={props.eprelUrl}
                        onClick={() => sendContentEvent(props.eprelUrl)}
                        target="_blank"
                      >
                        {translate(t => t.productPage.productInfoSheet)}
                      </ProductInfoSheet>
                    )}
                    {props.itemEprelImageURL && <EnergyLabelImage image={{ url: props.itemEprelImageURL }} />}
                  </EnergyInfoContent>
                </EnergyInfo>
              </EnergyInfoContainer>
            )}
          </>
        )}
      </RatingAndEnergyInfo>
    </Container>
  );
});

const Container = styled.div(
  createMinLargeViewportDecorator({
    padding: { top: t => t.sharedSpacing.m, x: 0 },
  }),
  {
    padding: { x: t => t.sharedSpacing.m, top: t => t.sharedSpacing.m },
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    fontWeight: 300,
  },
);

const BrandName = styled.div({
  fontSize: t => t.sharedFontSize.xs,
  fontWeight: 450,
  whiteSpace: 'nowrap',
});

const linkHoverDecorator = styled({
  ':hover': {
    textDecoration: 'underline',
  },
});

const BrandLink = styled(Link, linkHoverDecorator, {
  position: 'relative',
  display: 'inline-block',
});

const ProductName = styled.h1({
  fontSize: t => t.sharedFontSize.xl,
  marginBottom: 0,
  fontWeight: 450,
  lineHeight: 1.4,
});

const VariantName = styled.div({
  fontWeight: 300,
  fontSize: t => t.sharedFontSize.m,
});

const DesignerAndBrand = styled.div({
  marginBottom: 4,
  lineHeight: 1.3,
});

const Designers = styled.span({
  fontSize: t => t.sharedFontSize.xs,
  marginRight: 4,
});

const DesignerLink = styled(Link, linkHoverDecorator, {
  fontWeight: 450,
});

const RatingAndEnergyInfo = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  margin: { top: t => t.sharedSpacing.m, bottom: t => t.sharedSpacing.xl },
  position: 'relative',
});

const Stars = styled(RatingStars, {
  cursor: 'pointer',
});

const EnergyInfoContainer = styled.div({
  position: 'absolute',
  top: 0,
  right: 0,
  zIndex: ZIndex.Modal,
});

const EnergyInfo = styled.div({
  position: 'relative',
  boxShadow: 'rgba(0, 0, 0, 0.1) 10px 10px 40px 0px',
  backgroundColor: 'white',
  border: { xy: { style: 'solid', color: monochromeDark, width: 1 } },
  margin: { top: t => t.spacing.l },
});

const CloseButton = styled(Button, {
  position: 'absolute',
  right: 0,
  top: 0,
  zIndex: ZIndex.Modal + 1,
});

const EnergyInfoContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  padding: { xy: t => t.sharedSpacing.m },
});

const ProductInfoSheet = styled(Link, linkHoverDecorator, {
  fontSize: t => t.sharedFontSize.xxs,
  textDecoration: 'none',
  margin: { bottom: t => t.sharedSpacing.m },
});

const EnergyLabelImage = styled(Image, {
  width: 250,
});
